/**
 * Profile Settings Page - User can update profile, upload photo, and set theme
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Link2,
  ArrowLeft,
  User,
  Camera,
  Palette,
  Moon,
  Sun,
  Monitor,
  Check,
  Loader2,
  Save,
  Trash2,
  MapPin,
  FileText
} from 'lucide-react';
import PasskeySettings from "../components/PasskeySettings";

const ProfileSettings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const {
    colorTheme,
    changeColorTheme,
    availableThemes,
    appearanceMode,
    changeAppearanceMode,
    availableModes,
    isDarkMode
  } = useTheme();
  
  const [profile, setProfile] = useState({
    name: '',
    bio: '',
    location: ''
  });
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile/me');
      const data = response.data;
      setProfile({
        name: data.name || '',
        bio: data.bio || '',
        location: data.location || ''
      });
      setAvatar(data.profile_photo || data.avatar || null);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      // Fallback to user context
      if (user) {
        setProfile({
          name: user.name || '',
          bio: '',
          location: ''
        });
        setAvatar(user.profile_photo || user.avatar || null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/profile/me', profile);
      toast.success('Profile updated');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          // Remove the data URL prefix (e.g., "data:image/png;base64,")
          const base64Data = reader.result.split(',')[1];
          
          const response = await api.post('/profile/photo', {
            photo_data: base64Data,
            file_type: file.type
          });
          
          setAvatar(response.data.photo_url);
          toast.success('Profile photo updated');
          if (refreshUser) refreshUser();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload photo');
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    
    try {
      await api.delete('/profile/photo');
      setAvatar(null);
      toast.success('Profile photo removed');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleThemeChange = async (themeName) => {
    changeColorTheme(themeName);
    try {
      await api.put('/profile/theme', { color_theme: themeName });
    } catch (error) {
      console.error('Failed to save theme:', error);
    }
    toast.success(`Theme changed to ${availableThemes[themeName]?.name || themeName}`);
  };

  const handleModeChange = async (mode) => {
    changeAppearanceMode(mode);
    try {
      await api.put('/profile/theme', { appearance_mode: mode });
    } catch (error) {
      console.error('Failed to save mode:', error);
    }
    toast.success(`Appearance set to ${availableModes[mode]?.name || mode}`);
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'dark': return Moon;
      case 'light': return Sun;
      case 'system': return Monitor;
      default: return Moon;
    }
  };

  // Generate avatar URL with API prefix
  const getAvatarUrl = () => {
    if (!avatar) return null;
    if (avatar.startsWith('http')) return avatar;
    if (avatar.startsWith('/api')) return avatar;
    return avatar;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded border border-border">
                PROFILE
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-8">
        {/* Profile Photo Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Profile Photo
          </h2>
          
          <div className="flex items-center gap-6">
            <div className="relative">
              {getAvatarUrl() ? (
                <img
                  src={getAvatarUrl()}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-2 border-border"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center border-2 border-border">
                  <User className="h-10 w-10 text-muted-foreground" />
                </div>
              )}
              
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute bottom-0 right-0 p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-3">
                Upload a photo to personalize your profile. Max 5MB, JPG/PNG/GIF.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                  disabled={uploadingPhoto}
                >
                  Change Photo
                </Button>
                {avatar && (
                  <Button
                    onClick={handleDeletePhoto}
                    variant="outline"
                    size="sm"
                    className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Info Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Profile Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Name</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-border rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                <FileText className="h-4 w-4 inline mr-1" />
                Bio
              </label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-border rounded-lg text-slate-900 dark:text-slate-100 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={3}
                data-testid="bio-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                <MapPin className="h-4 w-4 inline mr-1" />
                Location
              </label>
              <input
                type="text"
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                placeholder="City, Country"
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-border rounded-lg text-slate-900 dark:text-slate-100 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="location-input"
              />
            </div>
            
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Passkey Settings Section */}
        <PasskeySettings />

        {/* Appearance Mode Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
            {isDarkMode() ? <Moon className="h-5 w-5 text-primary" /> : <Sun className="h-5 w-5 text-primary" />}
            Appearance Mode
          </h3>
          <p className="text-muted-foreground text-sm mb-6">
            Choose between dark mode, light mode, or follow your system settings.
          </p>
          
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(availableModes).map(([key, mode]) => {
              const ModeIcon = getModeIcon(key);
              return (
                <button
                  key={key}
                  onClick={() => handleModeChange(key)}
                  className={`relative p-4 rounded-lg border transition-all ${
                    appearanceMode === key
                      ? 'border-2 border-primary bg-muted/50'
                      : 'border-border hover:border-muted-foreground hover:bg-muted/30'
                  }`}
                  data-testid={`mode-${key}`}
                >
                  {appearanceMode === key && (
                    <div className="absolute top-2 right-2">
                      <Check className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div className="w-12 h-12 rounded-full mb-3 mx-auto bg-muted flex items-center justify-center">
                    <ModeIcon className={`h-6 w-6 ${
                      key === 'dark' ? 'text-indigo-400' : 
                      key === 'light' ? 'text-yellow-400' : 
                      'text-muted-foreground'
                    }`} />
                  </div>
                  <p className="text-foreground text-sm font-medium text-center">{mode.name}</p>
                  <p className="text-muted-foreground text-xs text-center mt-1">{mode.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Color Theme Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            Accent Color
          </h3>
          <p className="text-muted-foreground text-sm mb-6">
            Choose your preferred accent color. This will be applied throughout the app.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(availableThemes).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => handleThemeChange(key)}
                className={`relative p-4 rounded-lg border transition-all ${
                  colorTheme === key
                    ? 'border-2 border-primary bg-muted/50'
                    : 'border-border hover:border-muted-foreground hover:bg-muted/30'
                }`}
                data-testid={`theme-${key}`}
              >
                {colorTheme === key && (
                  <div className="absolute top-2 right-2">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div 
                  className="w-10 h-10 rounded-full mb-3 mx-auto shadow-lg"
                  style={{ backgroundColor: theme.primaryHex }}
                />
                <p className="text-foreground text-sm font-medium text-center">{theme.name}</p>
                <p className="text-muted-foreground text-xs text-center mt-1">{theme.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Preview Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Preview</h3>
          <p className="text-muted-foreground text-sm mb-4">
            See how your selected theme looks with various UI elements.
          </p>
          
          <div className="space-y-4">
            <div className="flex gap-3 flex-wrap">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                Primary Button
              </Button>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
                Outline Button
              </Button>
              <Button variant="ghost" className="text-primary hover:bg-primary/10">
                Ghost Button
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 rounded-full text-xs font-mono bg-primary/20 text-primary border border-primary/30">
                Status Badge
              </span>
              <span className="text-primary font-medium">Accent Text</span>
              <div className="w-4 h-4 rounded bg-primary" />
            </div>
            
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full w-2/3 bg-primary rounded-full" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileSettings;
